<template>
  <div id="sync-status">
    <div class="status" v-for="status in active_statuses" :key="status.id"
      :class="{'multi-status':active_statuses.length > 1}">
      <div class="status-type" >
        <div class="status-tag" :class="'bg-' + status.msg.type">
          {{ status.msg.type }}
        </div>

        <div v-if="active_statuses.length > 1" class="status-tag status-label bg-gray">
          {{ status.msg.meta.installation_id }}
        </div>

        <div :style="{opacity: status.dot_opacity}"
          class="status-dot" :class="'bg-' + status.msg.type"></div>
      </div>
      <div class="status-message">
        {{ status.msg.message }}
      </div>
    </div>

    <div v-if="active_statuses.length == 0" class="status-message">
      {{ server_status.message }}
    </div>

    <div class="status-help">
      This displays the content sync status of the on-site computer(s)
      connected to this screen.
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      /*
      store a mapping of socket id's : received status.
      stale statuses will be deleted after some time

      {
        'socket-id-abc123': {
          id: '<socketid>',
          msg: {} // full message from socketio event
          dot_opacity: 0,
          no_message_timeout: <timeout>,
          dot_timeout: <timeout>
        }
      }
      */
      statuses: {},

      // store socketio connection status of the widget itself
      server_status: {
        message: '',
        type: '',
      },

      socket: null,
      screen_id: this.$extension.entry.getSys().id,

      // how long to wait to receive a status message before removing status object
      // should be set e.g. 2x longer than status interval ms setting in sync app
      no_message_timeout_ms: 10000,
    };
  },
  computed: {
    active_statuses() {
      return Object.keys(this.statuses).map(key => this.statuses[key]);
    },
  },
  methods: {
    // trigger animation on dot
    beepDot(socketId) {
      const target = this.statuses[socketId];
      clearTimeout(target.dot_timeout);

      target.dot_opacity = 1;

      target.dot_timeout = setTimeout(() => {
        target.dot_opacity = 0;
      }, 1500);
    },
    // reset timeout which deletes inactive status objects
    resetTimeout(socketId) {
      const target = this.statuses[socketId];

      clearTimeout(target.no_message_timeout);

      target.no_message_timeout = setTimeout(() => {
        clearTimeout(target.no_message_timeout);
        clearTimeout(target.dot_timeout);

        this.$delete(this.statuses, socketId);
      }, this.no_message_timeout_ms);
    },

    markAsDisconnected() {
      this.server_status.message = 'Establishing connection to sync service...';
      this.server_status.type = 'disconnected';
    },
    markAsConnected() {
      this.server_status.message = 'Connected to sync service and awaiting updates...';
      this.server_status.type = 'connected';
    },
    init() {
      this.markAsConnected();

      // subscribe to updates from the screen's sync client
      this.socket.emit('subscribe to screen status', this.screen_id);
    },
  },
  created() {
    this.markAsDisconnected();

    // eslint-disable-next-line no-undef
    this.socket = io({
      query: {
        'x-socket-key': this.$extension.parameters.installation.socketio_connection_key,
      },
    });

    this.socket.on('connect', () => {
      // connect event is called on reconnection, too, so we're covering
      // initialization stuff in either case here
      this.init();
    });

    // this.socket.on('reconnect', this.init);
    this.socket.on('disconnect', this.markAsDisconnected);

    // handle socket status
    this.socket.on('status', (obj, sid) => {
      // new object received
      if (!this.statuses[sid]) {
        this.$set(this.statuses, sid, {
          id: sid,
          msg: obj,
          dot_opacity: 0,
          dot_timeout: null,
          no_message_timeout_ms: null,
        });
      }
      // update existing object
      else {
        this.$set(this.statuses[sid], 'msg', obj);
      }

      this.resetTimeout(sid);
      this.beepDot(sid);
    });
  },
};
</script>

<style lang="scss" scoped>

.status-type{
  display:flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
}
.status-tag {
  display: inline-block;
  padding: 3px 10px 0 10px;
  border-radius: 4px;
  text-transform: uppercase;
  font-size: 12px;
}
.status-dot {
  width: 5px;
  height: 5px;
  display: inline-block;
  margin-left: 5px;
  border-radius: 50px;
  opacity: 0;
  transition: opacity 500ms;
}

.status-help {
  color: #aaa;
  border-top: 1px solid #eee;
  padding-top: 15px;
  margin: 15px 0;
}

.status-label {
  margin-left: 5px;
}

.multi-status {
  margin-bottom: 20px;
}

.bg-gray {
  background-color: #a9a9a9;
  color: white;
}

.bg-ready {
  background-color: #0eb87f;
  color: white;
}

.bg-syncing {
  background-color: #ffd22f;
}

.bg-error {
  background-color: #cd3f39;
  color: white;
}

.bg-disconnected {
  background-color: #ccc;
}

.bg-connected {
  background-color: #ccc;
}
</style>
